import * as moment from "moment";
import { useEffect, useState, useCallback } from "react";
import { Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { userSignal } from "../../../signal/userSignal";
import { DEV_SERVER } from "../../../constants/urls";
import axios from "axios";
import { toast } from "react-toastify";
import { allUsersSignal } from "../../../signal/allUsersSignal";
import avatar1 from "../../../assets/images/avatars/01.png";
import DataTable from "react-data-table-component";
import { googleMapKey } from "../../../constants/googleMapKey";

export default function StaffAttendance() {
    const today = new Date();
    const formattedToday = moment(today).format("YYYY-MM-DD");

    const [startDate, setStartDate] = useState(formattedToday);
    const [endDate, setEndDate] = useState(formattedToday);
    const [attendanceData, setAttendanceData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false); // Initialize to false
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [locationCache, setLocationCache] = useState({});

    // Function to get location name from coordinates using Google Maps Geocoding API
    const getLocationName = async (lat, lon) => {
        const cacheKey = `${lat},${lon}`;

        // Cache check
        if (locationCache[cacheKey]) {
            return locationCache[cacheKey];
        }

        if (!lat || !lon || (lat === 0 && lon === 0)) {
            return "Location unavailable";
        }

        try {
            const response = await axios.get(
                `https://maps.googleapis.com/maps/api/geocode/json`,
                {
                    params: {
                        latlng: `${lat},${lon}`,
                        key: googleMapKey
                    }
                }
            );

            if (response.data && response.data.results && response.data.results.length > 0) {
                const addressComponents = response.data.results[0].address_components;
                
                let area = "";
                let city = "";

                // Extract area (sublocality/neighborhood) and city
                addressComponents.forEach(component => {
                    if (component.types.includes("sublocality") || 
                        component.types.includes("sublocality_level_1") ||
                        component.types.includes("neighborhood")) {
                        area = component.long_name;
                    }
                    if (component.types.includes("locality")) {
                        city = component.long_name;
                    }
                });

                const locationStr = area && city ? `${area}, ${city}` : 
                                  city ? city : 
                                  area ? area : "Location unavailable";

                setLocationCache(prev => ({ ...prev, [cacheKey]: locationStr }));
                return locationStr;
            }
        } catch (error) {
            console.error("Error fetching location:", error);
            return "Location unavailable";
        }

        return "Location unavailable";
    };


    // Create the base columns for the data table
    useEffect(() => {
        const baseColumns = [
            {
                name: "User",
                width: "250px",
                selector: (row) => {
                    if (row?.userId) {
                        return (
                            <div className="d-flex align-items-center" style={{ columnGap: "0.4rem" }}>
                                <img
                                    style={{ width: "20px", height: "20px" }}
                                    src={(row.userId.profileURL === "" || row.userId.profileURL === undefined) ? avatar1 : row.userId.profileURL}
                                    className="rounded-circle avatar-xs"
                                    alt="staff"
                                />
                                <OverlayTrigger placement="top" overlay={
                                    <Tooltip style={{ position: "fixed" }} id="tooltip-top">
                                        {row.userId.name}
                                    </Tooltip>}>
                                    <span style={{ width: "100%" }}>{row.userId.name}</span>
                                </OverlayTrigger>
                            </div>
                        )
                    }
                    return "-"
                }
            },
            {
                name: "Date",
                selector: (row) => moment(row.date).format("DD MMM YYYY") || "-",
                sortable: true,
            },
            {
                name: "Punch In",
                cell: (row) => {
                    const LocationCell = () => {
                        const [location, setLocation] = useState("");
                        const [showLocation, setShowLocation] = useState(false);
                        const [isLoading, setIsLoading] = useState(false);

                        const coords = row.punchInLocation?.coordinates || row.punchInLocation;
                        const hasCoords = coords && Array.isArray(coords) && coords.length === 2 && (coords[0] !== 0 || coords[1] !== 0);
                        const [lon, lat] = hasCoords ? coords : [0, 0];

                        const handleCoordinateClick = async () => {
                            if (!hasCoords) return;
                            
                            setShowLocation(true);
                            if (!location) {
                                setIsLoading(true);
                                const loc = await getLocationName(lat, lon);
                                setLocation(loc);
                                setIsLoading(false);
                            }
                        };

                        return (
                            <div style={{ lineHeight: "1.4" }}>
                                <div>{row.punchIn || "-"}</div>
                                {hasCoords ? (
                                    <div 
                                        onClick={handleCoordinateClick}
                                        style={{ 
                                            fontSize: "0.75rem", 
                                            color: showLocation ? "#666" : "#007bff", 
                                            marginTop: "2px",
                                            cursor: "pointer",
                                            textDecoration: showLocation ? "none" : "underline"
                                        }}
                                    >
                                        {showLocation ? (
                                            isLoading ? "Loading..." : location
                                        ) : (
                                            `${lat.toFixed(6)}, ${lon.toFixed(6)}`
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "2px" }}>
                                        Location unavailable
                                    </div>
                                )}
                            </div>
                        );
                    };
                    return <LocationCell />;
                },
                sortable: true,
            },
            {
                name: "Punch Out",
                cell: (row) => {
                    const LocationCell = () => {
                        const [location, setLocation] = useState("");
                        const [showLocation, setShowLocation] = useState(false);
                        const [isLoading, setIsLoading] = useState(false);

                        const coords = row.punchOutLocation?.coordinates || row.punchOutLocation;
                        const hasCoords = coords && Array.isArray(coords) && coords.length === 2 && (coords[0] !== 0 || coords[1] !== 0);
                        const [lon, lat] = hasCoords ? coords : [0, 0];

                        const handleCoordinateClick = async () => {
                            if (!hasCoords) return;
                            
                            setShowLocation(true);
                            if (!location) {
                                setIsLoading(true);
                                const loc = await getLocationName(lat, lon);
                                setLocation(loc);
                                setIsLoading(false);
                            }
                        };

                        return (
                            <div style={{ lineHeight: "1.4" }}>
                                <div>{row.punchOut || "-"}</div>
                                {hasCoords ? (
                                    <div 
                                        onClick={handleCoordinateClick}
                                        style={{ 
                                            fontSize: "0.75rem", 
                                            color: showLocation ? "#666" : "#007bff", 
                                            marginTop: "2px",
                                            cursor: "pointer",
                                            textDecoration: showLocation ? "none" : "underline"
                                        }}
                                    >
                                        {showLocation ? (
                                            isLoading ? "Loading..." : location
                                        ) : (
                                            `${lat.toFixed(6)}, ${lon.toFixed(6)}`
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "2px" }}>
                                        Location unavailable
                                    </div>
                                )}
                            </div>
                        );
                    };
                    return <LocationCell />;
                },
                sortable: true,
            },
            {
                name: "Working Hours",
                selector: (row) => row.totalWorkingHours || "-",
                sortable: true,
            },
            {
                name: "Status",
                selector: (row) => {
                    const status = row.status || "-";
                    let badgeClass = "bg-secondary";

                    if (status === "punch-in") badgeClass = "bg-success";
                    else if (status === "punch-out") badgeClass = "bg-info";
                    else if (status === "absent") badgeClass = "bg-danger";
                    else if (status === "leave") badgeClass = "bg-warning";

                    return <span className={`badge ${badgeClass}`}>{status}</span>;
                },
                sortable: true,
            },
            // {
            //     name: "Department",
            //     selector: (row) => {
            //         if (allUsersSignal.value[row.userId._id]?.state) {
            //             return (allUsersSignal.value[row.userId._id]?.state).toUpperCase();
            //         }
            //         return "-";
            //     }
            // },
            {
                name: "Designation",
                selector: (row) => {
                    if (allUsersSignal.value[row.userId._id]?.designation) {
                        return allUsersSignal.value[row.userId._id]?.designation;
                    }
                    return "-";
                }
            }
        ];
        setColumns(baseColumns);
    }, []);

    // Fetch attendance data
    const fetchAttendance = useCallback(async () => {
        try {
            setIsLoading(true);
            const params = {
                startDate,
                endDate,
                ...(selectedUserId && { userId: selectedUserId })
            };

            const resp = await axios.get(
                `${DEV_SERVER}/admin/v2/staff-attendance`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${userSignal.value?.token}`,
                    },
                }
            );

            if (resp.data.data && resp.data.data.length > 0) {
                console.log("Attendance data sample:", resp.data.data[0]); // Debug log
                console.log("PunchIn Location:", resp.data.data[0].punchInLocation);
                console.log("PunchOut Location:", resp.data.data[0].punchOutLocation);
                setAttendanceData(resp.data.data);
                toast.success("Attendance data fetched successfully");
            } else {
                setAttendanceData([]);
                toast.error("No attendance records found for selected criteria");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fetch attendance data");
            setAttendanceData([]);
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, selectedUserId]);

    // Remove the useEffect that automatically calls fetchAttendance
    // when hasSearched changes

    // Handle user row click
    const handleUserRowClick = (row) => {
        if (row.userId && row.userId._id) {
            // If clicking the same user again, deselect them
            if (selectedUserId === row.userId._id) {
                setSelectedUserId(null);
                fetchAttendance(); // Fetch all staff data when deselecting
            } else {
                setSelectedUserId(row.userId._id);
                fetchAttendance(); // Fetch only selected user data
            }
        }
    };

    // Filter attendance data by user name (case-insensitive)
    const filteredAttendanceData = attendanceData.filter(row => {
        if (!searchText) return true;
        const userName = row?.userId?.name || "";
        return userName.toLowerCase().includes(searchText.toLowerCase());
    });

    // Highlight selected user row
    const customRowStyles = [
        {
            when: row => selectedUserId && row.userId?._id === selectedUserId,
            style: {
                backgroundColor: "#e6f7ff",
            },
        },
    ];

    return (
        <>
            <div>
                <Row>
                    <Col sm="12">
                        <Card>
                            <Card.Header className="d-flex justify-content-between">
                                <div className="header-title">
                                    <h4 className="card-title">Staff Attendance</h4>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    <div style={{ display: "flex", alignItems: "flex-end", columnGap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                                        <div>
                                            <label htmlFor="searchName">Search Name</label>
                                            <input
                                                type="text"
                                                id="searchName"
                                                className="form-control"
                                                placeholder="Search by name"
                                                value={searchText}
                                                onChange={e => setSearchText(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="startDate">Start Date</label>
                                            <input
                                                type="date"
                                                id="startDate"
                                                className="form-control vanila-datepicker"
                                                placeholder="Start Date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="endDate">End Date</label>
                                            <input
                                                type="date"
                                                id="endDate"
                                                className="form-control vanila-datepicker"
                                                placeholder="End Date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </div>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                setSelectedUserId(null);
                                                setHasSearched(true);
                                                fetchAttendance(); // Explicitly call the API when button is clicked
                                            }}
                                            disabled={isLoading}
                                        >
                                            {selectedUserId ? "Show All Staff" : "Attendance"}
                                        </button>
                                    </div>
                                </div>

                                {isLoading ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : hasSearched && filteredAttendanceData.length > 0 ? (
                                    <>
                                        <DataTable
                                            columns={columns}
                                            data={filteredAttendanceData}
                                            pagination={true}
                                            paginationPerPage={35}
                                            paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 35]}
                                            responsive={true}
                                            highlightOnHover={true}
                                            striped={true}
                                            customStyles={{ rows: { style: { cursor: "pointer" } } }}
                                            onRowClicked={handleUserRowClick}
                                            rowClass={customRowStyles}
                                        />
                                    </>
                                ) : null}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
}